import React from 'react';

/**
 * Render HTML admin content safely in the browser.
 * Strips <script> tags and event-handler attributes to reduce XSS risk.
 * Allows basic formatting tags: p, br, strong, b, em, i, u, s, strike,
 * ul, ol, li, h1-h6, blockquote, pre, code, a, span, div.
 */
function sanitizeHtml(raw: string): string {
  if (typeof window === 'undefined') {
    // SSR: return escaped text only. Browser will hydrate with sanitized HTML.
    return raw
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(raw, 'text/html');

  // Remove script/style tags entirely
  const dangerousTags = ['script', 'style', 'iframe', 'object', 'embed', 'form'];
  dangerousTags.forEach((tag) => {
    doc.querySelectorAll(tag).forEach((el) => el.remove());
  });

  // Remove event-handler attributes and javascript: links
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_ELEMENT);
  let node: Element | null = walker.currentNode as Element;
  while (node) {
    Array.from(node.attributes).forEach((attr) => {
      const name = attr.name.toLowerCase();
      if (name.startsWith('on') || (name === 'href' && attr.value.trim().toLowerCase().startsWith('javascript:'))) {
        node!.removeAttribute(attr.name);
      }
    });
    node = walker.nextNode() as Element | null;
  }

  return doc.body.innerHTML;
}

interface SafeHtmlProps {
  html: string;
  className?: string;
  as?: keyof JSX.IntrinsicElements;
}

export default function SafeHtml({ html, className, as: Component = 'div' }: SafeHtmlProps) {
  const sanitized = sanitizeHtml(html || '');
  return React.createElement(Component as string, {
    className,
    dangerouslySetInnerHTML: { __html: sanitized },
  });
}
